import type { PersonDto } from "./benefit-types"
import { educationLabels, eligibilityLabels, ethnicityLabels, medicalInsuranceLabels, workStatusLabels } from "./benefit-enums"
import { hasDisabilityFlag, NOT_ANSWERED } from "./household"

type Demographics = Pick<PersonDto, "ethnicity" | "workStatus" | "educationLevel" | "medicalInsurance" | "eligibilityStatus" | "otherCharacteristics">

function answer(labels: Record<number, string>, value: number | null | undefined): string {
  if (value == null || value === NOT_ANSWERED) return "Not answered"
  return labels[value] ?? `Unrecognized (${value})`
}

/** Explain the D/V flags used by CertifiedCommerceAsciiRenderer, retaining every recorded code. */
function characteristicsDisplay(value: string | null | undefined): string {
  const recorded = value?.trim()
  if (!recorded) return "Not answered"
  const flags = [
    ...(hasDisabilityFlag(recorded) ? [{ code: "D", label: "Disability" }] : []),
    ...(recorded.toUpperCase().includes("V") ? [{ code: "V", label: "Military veteran" }] : []),
  ]
  if (/^[dv]+$/i.test(recorded)) return `${flags.map(flag => flag.label).join(", ")} (${recorded})`
  // Do not decode, discard or normalize unfamiliar codes or free text.
  return `Recorded: ${recorded}${flags.length ? ` · ${flags.map(flag => `${flag.code} = ${flag.label.toLowerCase()}`).join("; ")}` : ""}`
}

/** Same fields for every member; absent answers never turn into a negative answer. */
export function householdMemberSummary(person: Demographics): { label: string; value: string }[] {
  return [
    { label: "Ethnicity", value: answer(ethnicityLabels, person.ethnicity) },
    { label: "Work Status", value: answer(workStatusLabels, person.workStatus) },
    { label: "Education", value: answer(educationLabels, person.educationLevel) },
    { label: "Medical Insurance", value: answer(medicalInsuranceLabels, person.medicalInsurance) },
    { label: "LIHEAP Eligibility", value: answer(eligibilityLabels, person.eligibilityStatus) },
    // The legacy flag string does not distinguish an unanswered checkbox from an explicit No.
    { label: "Disability", value: hasDisabilityFlag(person.otherCharacteristics) ? "Indicated" : "Not indicated" },
    { label: "Other Characteristics", value: characteristicsDisplay(person.otherCharacteristics) },
  ]
}
